import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../hooks/useNotifications';
import { Bell, X, CheckCheck, Menu } from 'lucide-react';
import api from '../services/api';
import ChangePasswordModal from './ChangePasswordModal';

const ROLES_GLOBALES = ['ADMIN', 'DIRECTOR', 'GERENTE'];
const ROLES_GESTORES = ['ADMIN', 'DIRECTOR', 'GERENTE'];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { notifications, unread, markAllRead, refetch } = useNotifications(user);
  const [showNotif, setShowNotif] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const notifRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotif(false);
      if (menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => { logout(); navigate('/login'); };

  const toggleNotifCorreo = async () => {
    try {
      const res = await api.get('/auth/me');
      const current = res.data.notificacionesCorreo;
      await api.patch('/auth/me/notificaciones', { notificacionesCorreo: !current });
      alert(!current ? '✅ Notificaciones por correo activadas' : '🔕 Notificaciones por correo desactivadas');
    } catch (e) {
      alert('Error al actualizar preferencia');
    }
  };
  const handleNotifClick = async (n) => {
    try { await api.patch(`/notifications/${n.id}/read`); } catch {}
    refetch();
    setShowNotif(false);
    if (n.url) navigate(n.url);
  };

  const navItems = [
    { path: '/', label: 'Dashboard', roles: ROLES_GLOBALES },
    { path: '/solicitudes', label: 'Solicitudes', roles: ['ADMIN', 'DIRECTOR', 'GERENTE', 'SUPERVISOR', 'OPERADOR'] },
    { path: '/actividades', label: 'Actividades', roles: ['ADMIN', 'DIRECTOR', 'GERENTE', 'SUPERVISOR', 'OPERADOR'] },
    { path: '/reportes', label: 'Reportes', roles: ROLES_GLOBALES },
    { path: '/usuarios', label: 'Usuarios y Áreas', roles: ROLES_GESTORES },
  ].filter(item => item.roles.includes(user?.role));

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: "'Segoe UI', sans-serif" }}>
      <style>{`
        @media (max-width: 768px) {
          .nav-links { display: none !important; }
          .nav-user-name { display: none !important; }
          .hamburger { display: flex !important; }
          .mobile-menu { display: block !important; }
          .notif-panel {
            position: fixed !important;
            top: 68px !important;
            left: 12px !important;
            right: 12px !important;
            width: auto !important;
          }
        }
        @media (min-width: 769px) {
          .hamburger { display: none !important; }
          .mobile-menu { display: none !important; }
        }
      `}</style>

      <nav style={{ background: 'linear-gradient(135deg, #1a2f6b 0%, #2563eb 100%)', boxShadow: '0 2px 16px rgba(26,47,107,0.3)', position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 60 }}>

          {/* Logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img src="/Logo_Dash.png" alt="ServiceFlow" style={{ height: 36 }} />
            <div>
              <div style={{ color: 'white', fontWeight: 800, fontSize: 16, lineHeight: 1 }}>Service<span style={{ color: '#4ade80' }}>Flow</span></div>
              <div style={{ color: '#93c5fd', fontSize: 10 }}>Gestión de Servicios Internos</div>
            </div>
          </div>

          {/* Navegación desktop */}
          <div className="nav-links" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {navItems.map(item => {
              const isActive = location.pathname === item.path;
              return (
                <Link key={item.path} to={item.path} style={{ color: isActive ? '#1a2f6b' : 'white', background: isActive ? 'white' : 'transparent', padding: '6px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>
                  {item.label}
                </Link>
              );
            })}
          </div>

          {/* Derecha */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>

            {/* Campanita */}
            <div ref={notifRef} style={{ position: 'relative' }}>
              <button onClick={() => { setShowNotif(!showNotif); setShowMenu(false); }}
                style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 10, width: 38, height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', position: 'relative' }}>
                <Bell size={17} color="white" />
                {unread > 0 && (
                  <span style={{ position: 'absolute', top: -4, right: -4, background: '#ef4444', color: 'white', borderRadius: '50%', width: 17, height: 17, fontSize: 9, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #1a2f6b' }}>
                    {unread > 9 ? '9+' : unread}
                  </span>
                )}
              </button>

              {/* Panel notificaciones */}
              {showNotif && (
                <div className="notif-panel" style={{ position: 'absolute', top: 46, right: 0, width: 320, background: 'white', borderRadius: 16, boxShadow: '0 8px 32px rgba(0,0,0,0.15)', border: '1px solid #e2e8f0', zIndex: 200, overflow: 'hidden' }}>
                  <div style={{ padding: '12px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 700, color: '#1a2f6b', fontSize: 13 }}>
                      Notificaciones
                      {unread > 0 && <span style={{ background: '#ef4444', color: 'white', borderRadius: 10, padding: '1px 6px', fontSize: 10, marginLeft: 6 }}>{unread}</span>}
                    </span>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {unread > 0 && (
                        <button onClick={markAllRead} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#2563eb', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
                          <CheckCheck size={13} /> Leídas
                        </button>
                      )}
                      <button onClick={() => setShowNotif(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}><X size={15} /></button>
                    </div>
                  </div>
                  <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                    {notifications.length === 0 ? (
                      <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>Sin notificaciones</div>
                    ) : notifications.map(n => (
                      <div key={n.id} onClick={() => handleNotifClick(n)}
                        style={{ padding: '10px 14px', cursor: 'pointer', background: n.read ? 'white' : '#eff6ff', borderBottom: '1px solid #f8fafc', borderLeft: n.read ? 'none' : '3px solid #2563eb' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                        onMouseLeave={e => e.currentTarget.style.background = n.read ? 'white' : '#eff6ff'}
                      >
                        <div style={{ fontWeight: n.read ? 500 : 700, color: '#1e293b', fontSize: 12, marginBottom: 2 }}>{n.title}</div>
                        <div style={{ color: '#64748b', fontSize: 11 }}>{n.body}</div>
                        <div style={{ color: '#94a3b8', fontSize: 10, marginTop: 3 }}>{new Date(n.createdAt).toLocaleString('es-MX')}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Usuario — solo desktop */}
            <div className="nav-user-name" style={{ textAlign: 'right' }}>
              <div style={{ color: 'white', fontSize: 12, fontWeight: 600 }}>{user?.name}</div>
              <div style={{ color: '#93c5fd', fontSize: 10 }}>{user?.role}</div>
            </div>

            {/* Avatar */}
            <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: 14, flexShrink: 0 }}>
              {user?.name?.charAt(0).toUpperCase()}
            </div>

           {/* Notificaciones correo — solo desktop */}
            <button onClick={toggleNotifCorreo} className="nav-user-name"
              style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: 'white', fontSize: 12, fontWeight: 600, padding: '6px 12px', borderRadius: 8, cursor: 'pointer' }}
              title="Activar/desactivar notificaciones por correo">
              🔔
            </button>
          
           {/* Cambiar contraseña — solo desktop */}
            <button onClick={() => setShowChangePassword(true)} className="nav-user-name"
              style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: 'white', fontSize: 12, fontWeight: 600, padding: '6px 12px', borderRadius: 8, cursor: 'pointer' }}>
              Contraseña
            </button>

          {/* Salir — solo desktop */}
            <button onClick={handleLogout} className="nav-user-name"
              style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: 'white', fontSize: 12, fontWeight: 600, padding: '6px 12px', borderRadius: 8, cursor: 'pointer' }}>
              Salir
            </button>

            {/* Hamburguesa — solo móvil */}
            <div ref={menuRef} style={{ position: 'relative' }}>
              <button className="hamburger"
                onClick={() => { setShowMenu(!showMenu); setShowNotif(false); }}
                style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 10, width: 38, height: 38, alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                <Menu size={18} color="white" />
              </button>

              {showMenu && (
                <div className="mobile-menu" style={{ position: 'fixed', top: 68, right: 12, left: 12, background: 'white', borderRadius: 16, boxShadow: '0 8px 32px rgba(0,0,0,0.15)', border: '1px solid #e2e8f0', zIndex: 200, overflow: 'hidden' }}>
                  <div style={{ padding: '12px 16px', borderBottom: '1px solid #f1f5f9' }}>
                    <div style={{ fontWeight: 700, color: '#1a2f6b', fontSize: 13 }}>{user?.name}</div>
                    <div style={{ color: '#64748b', fontSize: 11 }}>{user?.role}</div>
                  </div>
                  {navItems.map(item => {
                    const isActive = location.pathname === item.path;
                    return (
                      <Link key={item.path} to={item.path}
                        onClick={() => setShowMenu(false)}
                        style={{ display: 'block', padding: '12px 16px', fontSize: 14, fontWeight: isActive ? 700 : 500, color: isActive ? '#2563eb' : '#374151', textDecoration: 'none', background: isActive ? '#eff6ff' : 'white', borderBottom: '1px solid #f8fafc' }}>
                        {item.label}
                      </Link>
                    );
                  })}
                  <button onClick={toggleNotifCorreo}
                    style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: 'white', fontSize: 12, fontWeight: 600, padding: '6px 12px', borderRadius: 8, cursor: 'pointer' }}
                    title="Activar/desactivar notificaciones por correo">
                   🔔
                  </button>
                  <button onClick={() => { setShowChangePassword(true); setShowMenu(false); }}
                    style={{ width: '100%', padding: '12px 16px', fontSize: 14, fontWeight: 600, color: '#374151', background: 'white', border: 'none', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit', borderBottom: '1px solid #f8fafc' }}>
                    Cambiar contraseña
                  </button>
                  <button onClick={handleLogout}
                    style={{ width: '100%', padding: '12px 16px', fontSize: 14, fontWeight: 600, color: '#dc2626', background: 'white', border: 'none', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit' }}>
                    Cerrar sesión
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>
      {showChangePassword && <ChangePasswordModal onClose={() => setShowChangePassword(false)} />}          
      <main style={{ maxWidth: 1280, margin: '0 auto', padding: '20px 16px' }}>
        {children}
      </main>
    </div>
  );
}